import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ebb_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors and other response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      localStorage.removeItem('ebb_token');
      localStorage.removeItem('ebb_user');
      localStorage.removeItem('ebb_admin');
      
      // Only redirect if not already on home or login page
      const currentPath = window.location.pathname;
      if (currentPath !== '/' && !currentPath.includes('/login')) {
        window.location.href = '/';
      }
    }
    
    // Handle forbidden errors (admin access)
    if (error.response?.status === 403) {
      console.warn('Access forbidden:', error.response?.data?.detail);
    }
    
    // Log errors in development
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message,
        url: error.config?.url
      });
    }
    
    return Promise.reject(error);
  }
);

// Auth
export const register = async (userData) => {
  const response = await api.post('/auth/register', userData);
  return response.data;
};

export const login = async (credentials) => {
  const response = await api.post('/auth/login', credentials);
  return response.data;
};

export const adminLogin = async (credentials) => {
  const response = await api.post('/admin/login', credentials);
  return response.data;
};

export const setupAdmin = async (adminData) => {
  const response = await api.post('/admin/setup', adminData);
  return response.data;
};

export const checkAdmin = async () => {
  const response = await api.get('/auth/check-admin');
  return response.data;
};

export const getMe = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

export const updateMe = async (userData) => {
  const response = await api.put('/auth/me', userData);
  return response.data;
};

// Addresses
export const getAddresses = async () => {
  const response = await api.get('/auth/addresses');
  return response.data;
};

export const addAddress = async (address) => {
  const response = await api.post('/auth/addresses', address);
  return response.data;
};

export const updateAddress = async (addressId, address) => {
  const response = await api.put(`/auth/addresses/${addressId}`, address);
  return response.data;
};

export const deleteAddress = async (addressId) => {
  const response = await api.delete(`/auth/addresses/${addressId}`);
  return response.data;
};

// Wishlist
export const getWishlist = async () => {
  const response = await api.get('/wishlist');
  return response.data;
};

export const addToWishlist = async (productId) => {
  const response = await api.post(`/wishlist/${productId}`);
  return response.data;
};

export const removeFromWishlist = async (productId) => {
  const response = await api.delete(`/wishlist/${productId}`);
  return response.data;
};

// Loyalty
export const getLoyaltyStatus = async () => {
  const response = await api.get('/loyalty');
  return response.data;
};

// User Orders
export const getUserOrders = async () => {
  const response = await api.get('/user/orders');
  return response.data;
};

// Products
export const getProducts = async (params = {}) => {
  const response = await api.get('/products', { params });
  return response.data;
};

export const getProduct = async (productId) => {
  const response = await api.get(`/products/${productId}`);
  return response.data;
};

export const createProduct = async (product) => {
  const response = await api.post('/products', product);
  return response.data;
};

export const updateProduct = async (productId, product) => {
  const response = await api.put(`/products/${productId}`, product);
  return response.data;
};

export const deleteProduct = async (productId) => {
  const response = await api.delete(`/products/${productId}`);
  return response.data;
};

export const getCategories = async (activeOnly = false) => {
  const response = await api.get('/categories', { params: { active_only: activeOnly } });
  return response.data;
};

export const getCategory = async (categoryId) => {
  const response = await api.get(`/categories/${categoryId}`);
  return response.data;
};

export const getCategoryBySlug = async (slug) => {
  const response = await api.get(`/categories/slug/${slug}`);
  return response.data;
};

export const createCategory = async (categoryData) => {
  console.log('[DEBUG API] createCategory called with:', categoryData);
  // Normalize empty image to null
  const normalizedData = {
    ...categoryData,
    image: categoryData.image || null
  };
  console.log('[DEBUG API] Normalized data:', normalizedData);
  const response = await api.post('/categories', normalizedData);
  console.log('[DEBUG API] createCategory response:', response.data);
  return response.data;
};

export const updateCategory = async (categoryId, categoryData) => {
  const response = await api.put(`/categories/${categoryId}`, categoryData);
  return response.data;
};

export const toggleCategoryActive = async (categoryId) => {
  const response = await api.patch(`/categories/${categoryId}/toggle-active`);
  return response.data;
};

export const deleteCategory = async (categoryId) => {
  const response = await api.delete(`/categories/${categoryId}`);
  return response.data;
};

export const reorderCategories = async (categoryIds) => {
  const response = await api.post('/categories/reorder', { category_ids: categoryIds });
  return response.data;
};

export const seedDefaultCategories = async () => {
  const response = await api.post('/categories/seed-default');
  return response.data;
};

// Cart
export const getCart = async (sessionId) => {
  const response = await api.get(`/cart/${sessionId}`);
  return response.data;
};

export const addToCart = async (sessionId, item) => {
  const response = await api.post(`/cart/${sessionId}/add`, item);
  return response.data;
};

export const updateCartItem = async (sessionId, item) => {
  const response = await api.put(`/cart/${sessionId}/update`, item);
  return response.data;
};

export const removeFromCart = async (sessionId, productId, size, color) => {
  const response = await api.delete(`/cart/${sessionId}/item`, {
    params: { product_id: productId, size, color },
  });
  return response.data;
};

export const clearCart = async (sessionId) => {
  const response = await api.delete(`/cart/${sessionId}`);
  return response.data;
};

// Orders
export const createOrder = async (order) => {
  const response = await api.post('/orders', order);
  return response.data;
};

export const getOrder = async (orderId) => {
  const response = await api.get(`/orders/${orderId}`);
  return response.data;
};

export const getOrders = async (params = {}) => {
  const response = await api.get('/orders', { params });
  return response.data;
};

export const updateOrderStatus = async (orderId, status) => {
  const response = await api.put(`/orders/${orderId}/status?status=${status}`);
  return response.data;
};

// Payment QR Code
export const getPaymentQR = async (orderId) => {
  const response = await api.get(`/payment/qrcode/${orderId}`);
  return response.data;
};

// Settings
export const getPaymentSettings = async () => {
  const response = await api.get('/settings/payment');
  return response.data;
};

export const updatePaymentSettings = async (settings) => {
  const response = await api.put('/settings/payment', settings);
  return response.data;
};

export const getSiteSettings = async () => {
  const response = await api.get('/settings/site');
  return response.data;
};

export const updateSiteSettings = async (settings) => {
  const response = await api.put('/settings/site', settings);
  return response.data;
};

// Admin Dashboard
export const getAdminDashboard = async () => {
  const response = await api.get('/admin/dashboard');
  return response.data;
};

export const getStockAlerts = async () => {
  const response = await api.get('/admin/stock/alerts');
  return response.data;
};

// Image Upload
export const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/upload/image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const uploadMultipleImages = async (files) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });
  
  const response = await api.post('/upload/images', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteUploadedImage = async (filename) => {
  const response = await api.delete(`/uploads/${filename}`);
  return response.data;
};

export const listUploadedImages = async () => {
  const response = await api.get('/uploads');
  return response.data;
};

// Seed database
export const seedDatabase = async () => {
  const response = await api.post('/seed');
  return response.data;
};

// Gift Cards
export const validateGiftCard = async (code) => {
  const response = await api.get(`/gift-cards/validate/${code}`);
  return response.data;
};

export const redeemGiftCard = async (code, amount) => {
  const response = await api.post(`/gift-cards/redeem?amount=${amount}`, { code });
  return response.data;
};

// Order Management
export const deleteOrder = async (orderId, deleteInvoice = true) => {
  const response = await api.delete(`/orders/${orderId}?delete_invoice=${deleteInvoice}`);
  return response.data;
};

// Statistics
export const getOrderStatistics = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.startDate) queryParams.append('start_date', params.startDate);
  if (params.endDate) queryParams.append('end_date', params.endDate);
  if (params.customerName) queryParams.append('customer_name', params.customerName);
  
  const response = await api.get(`/orders/statistics?${queryParams.toString()}`);
  return response.data;
};

export default api;
